import type { CSSProperties } from 'react'
import { memo, Fragment, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { getTrustIndicatorsContent } from './helpers'
import { rem } from 'polished'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import eases from 'styles/tools/eases'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import clsx from 'clsx'
import Group from 'components/utils/Group'
import { GERMAN_LOCALE } from 'i18n/util/locale'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const SLIDE_DURATION_MS = 4000

const Container = styled.div`
  overflow: hidden;
`

const TrustIndicatorsMobile = styled.div<{ $slideCount: number }>`
  --trust-indicator-section-carousel-direction: 1;

  display: grid;
  gap: ${rem(16)};
  overflow: visible;
  grid-template-columns: repeat(${(props) => props.$slideCount}, 100%);
  width: 100%;
  transition: transform 0.5s ${eases.power2.inOut};

  [dir='rtl'] & {
    --trust-indicator-section-carousel-direction: -1;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: unset;
    grid-auto-columns: none;
    grid-auto-flow: column;
  }
`

const TrustIndicatorsDesktop = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  align-items: center;
  gap: ${rem(16)};

  > * {
    min-width: 0;
    width: auto;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  &.padding {
    ${mediaQueryUp.desktop} {
      padding: 0 ${rem(100)};
    }
  }
`

function TrustIndicatorSection() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const trustIndicators = useMemo(() => {
    return getTrustIndicatorsContent(currentRegionCode).filter(
      (trustIndicator) => trustIndicator.show,
    )
  }, [currentRegionCode])

  const isGermanLocale = intl.locale === GERMAN_LOCALE
  const indicatorsPerSlide = isGermanLocale ? 1 : 2
  const slideCount = Math.ceil(trustIndicators.length / indicatorsPerSlide)

  const mobileSlides = useMemo(() => {
    const slides = Array.from({ length: slideCount }, (_, index) => (
      <Group
        key={index}
        direction="horizontal"
        horizontalAlign="stretch"
        verticalAlign="center"
      >
        {trustIndicators.slice(index * indicatorsPerSlide, (index + 1) * indicatorsPerSlide).map(({ Component, key }) => (
          <Fragment key={key}>{Component}</Fragment>
        ))}
      </Group>
    ))
    return slides
  }, [indicatorsPerSlide, slideCount, trustIndicators])

  const isCarouselEnabled = !useScreenSizeUp('tablet')
  const { activeIndex, goToSlide, paused, setPaused } = useCarouselAutoPlay(
    slideCount,
    SLIDE_DURATION_MS,
    { enabled: isCarouselEnabled },
  )

  const slideStyle: CSSProperties | undefined = isCarouselEnabled
    ? {
        transform: `translateX(calc(${-activeIndex} * (100% + ${rem(16)}) * var(--trust-indicator-section-carousel-direction)))`,
      }
    : undefined

  return (
    <Container>
      <CSSBreakpoint max="mobile">
        <VerticalSpacer gap={20}>
          <TrustIndicatorsMobile $slideCount={slideCount} style={slideStyle}>
            {mobileSlides}
          </TrustIndicatorsMobile>
          {slideCount > 1 && <PaginationDots
            count={slideCount}
            activeIndex={activeIndex}
            slideDuration={SLIDE_DURATION_MS}
            onSelect={goToSlide}
            paused={paused}
            onHoverChange={setPaused}
          />}
        </VerticalSpacer>
      </CSSBreakpoint>
      <CSSBreakpoint min="tablet">
        <TrustIndicatorsDesktop
          className={clsx({ padding: trustIndicators.length < 3 })}
        >
          {trustIndicators.map(({ Component, key }) => (
            <Fragment key={key}>{Component}</Fragment>
          ))}
        </TrustIndicatorsDesktop>
      </CSSBreakpoint>
    </Container>
  )
}

export default memo(TrustIndicatorSection)
